import { api } from '../utils/api';
import type { MitigationFactorId } from './interpretationApi';

/**
 * Mitigation CREDIT write-path client — the ENGINE axis (owner ruling 2026-09-21).
 *
 * DELIBERATELY separate from interpretationApi.ts (the CONCEDE / mediator axis). Same
 * factor keys, OPPOSITE money:
 *   CREDIT  → the insured's mitigation is documented → that factor's failure-to-mitigate
 *             deduction drops → the carrier FLOOR rises. Writes the engine
 *             `mitigation_factors` override + full-pipeline rerun. NEVER touches the
 *             interpretation `p`.
 *   CONCEDE → interpretationApi.patchMitigationAttestation (p=1) → mediator E[X] down.
 *
 * Backend: backend/app/api/v1/mitigation.py
 *   POST /api/v1/mitigation/{report_id}/factors/{key}/credit        (cite required)
 *   POST /api/v1/mitigation/{report_id}/factors/{key}/clear-credit
 * Accountant-only + first-party + cite. Throws on 4xx (403 role, 409 conceded/third-party,
 * 422 bad factor) — the message is the FastAPI `detail`.
 */

export interface MitigationCreditResponse {
  status: 'credited' | 'cleared';
  report_id: string;
  factor_key: MitigationFactorId;
  override_id?: string;
  credited_factors?: Record<string, unknown>;
  new_mitigation_credit: number | null;
  new_carrier_floor: number | null;
  net_claim: number | null;
  run_id?: string | null;
  note?: string;
}

/** CREDIT one mitigation factor (insured mitigated → carrier floor up). `cite` is the
 * accountant's attestation basis (≥10 chars — verbatim evidence + document reference). */
export async function creditMitigationFactor(
  reportId: string,
  factorKey: MitigationFactorId,
  cite: string,
  docId?: string,
): Promise<MitigationCreditResponse> {
  return api.post<MitigationCreditResponse>(
    `/api/v1/mitigation/${reportId}/factors/${factorKey}/credit`,
    { cite, doc_id: docId ?? null },
    { showErrorToast: false },
  );
}

/** Reverse a prior CREDIT (deduction re-applies → carrier floor drops back). */
export async function clearMitigationCredit(
  reportId: string,
  factorKey: MitigationFactorId,
): Promise<MitigationCreditResponse> {
  return api.post<MitigationCreditResponse>(
    `/api/v1/mitigation/${reportId}/factors/${factorKey}/clear-credit`,
    {},
    { showErrorToast: false },
  );
}
